"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
  BillingInterval,
  billingIntervalForVariant,
  isMonthlyDefaultExperimentEligible,
  MONTHLY_DEFAULT_FLAG_KEY,
} from "../helpers/billingIntervalExperiment";
import { useProfile } from "./useProfile";

const OVERRIDE_FEATURE_FLAGS_KEY = "$override_feature_flags";
const STORED_PERSON_PROPERTIES_KEY = "$stored_person_properties";

// The interval PricingCards opens on, from the "pricing-default-monthly"
// experiment (see helpers/billingIntervalExperiment.ts):
// - Only eligible profiles (free, non-guest, created on/after the launch
//   date) evaluate the flag. Everyone else gets Yearly without touching it.
// - Exposure is the $feature_flag_called that getFeatureFlag records here.
//   Only PricingCards mounts this hook, so an exposed user is one who saw the
//   pricing cards (paywall modal or /pricing).
// - The flag's release conditions match on signed_up_at / is_guest, fed via
//   setPersonPropertiesForFlags so evaluation never waits on person-property
//   ingestion (same values usePricingLimitVariant sets).
// - No experience continuity: logged-out visitors never evaluate it, and
//   after identify() every evaluation hashes the profile id.
// Toolbar / console overrides win over the eligibility gate (local QA).
// Returns Yearly until the profile and flags have loaded.
export const useDefaultBillingInterval = (): BillingInterval => {
  const posthog = usePostHog();
  const { data: profile } = useProfile();
  const [flagVariant, setFlagVariant] = useState<
    string | boolean | undefined
  >();

  const isEligible = isMonthlyDefaultExperimentEligible(profile);
  const signedUpAt =
    isEligible && profile?.createdAt
      ? new Date(profile.createdAt).toISOString()
      : undefined;

  // Guarded against re-sending identical values: setPersonPropertiesForFlags
  // (_, true) reloads flags, and usePricingLimitVariant and
  // useReframeFocusVariant set the same values.
  useEffect(() => {
    if (!posthog || !signedUpAt) return;
    const stored = posthog.get_property?.(STORED_PERSON_PROPERTIES_KEY) as
      | Record<string, string>
      | undefined;
    if (stored?.signed_up_at === signedUpAt && stored?.is_guest === "false")
      return;
    posthog.setPersonPropertiesForFlags(
      { signed_up_at: signedUpAt, is_guest: "false" },
      true,
    );
  }, [posthog, signedUpAt]);

  useEffect(() => {
    if (!posthog) return;
    const update = () => {
      const overrides = posthog.get_property?.(OVERRIDE_FEATURE_FLAGS_KEY) as
        | Record<string, string | boolean>
        | undefined;
      const hasToolbarOverride =
        !!overrides &&
        typeof overrides === "object" &&
        Object.prototype.hasOwnProperty.call(
          overrides,
          MONTHLY_DEFAULT_FLAG_KEY,
        );
      setFlagVariant(
        hasToolbarOverride || isEligible
          ? posthog.getFeatureFlag(MONTHLY_DEFAULT_FLAG_KEY)
          : undefined,
      );
    };
    update();
    return posthog.onFeatureFlags(update);
  }, [posthog, isEligible]);

  return billingIntervalForVariant(flagVariant);
};
